// src/components/Admin/Category/PricingRulesSection.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
    getPricingRulesForSetting,
    createPricingRule,
    updatePricingRule,
    deletePricingRule
} from '../../../../services/pricingApi';
import { Button } from '../../../ui/button'; // Updated
import PricingRuleList from './PricingRuleList';
import PricingRuleForm from './PricingRuleForm';
import { Alert, AlertDescription, AlertTitle } from '../../../ui/alert'; // Updated [cite: 25, 26]
import { Terminal } from "lucide-react"; // Example Icon for Alert
import { useAdminStore } from '../../../../store/adminStore'; 

// Mock toast for now, replace with actual shadcn/ui toast or your preferred library
const toast = {
    success: (message) => console.log(`Toast Success: ${message}`),
    error: (message) => console.error(`Toast Error: ${message}`),
}

export default function PricingRulesSection({ categorySettingId, categoryId, questionList, canEditRules }) {
    const [rules, setRules] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [showRuleForm, setShowRuleForm] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const locale = useAdminStore((state) => state.locale); // <-- GET CONTEXT
    console.log("CategorySettingID", categorySettingId);
    console.log("CategoryID", categoryId);
    

    const fetchRules = useCallback(async () => {
        if (!categorySettingId) {
            setRules([]);
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        setError('');
        try {
            const response = await getPricingRulesForSetting(categorySettingId, { active_only: false, locale: locale});
            setRules(response.data || []);
        } catch (err) {
            const errorMsg = `Failed to fetch pricing rules: ${err.response?.data?.detail || err.message}`;
            setError(errorMsg);
            toast.error(errorMsg);
            console.error("Fetch rules error:", err);
        } finally {
            setIsLoading(false);
        }
    }, [categorySettingId]);

    useEffect(() => {
        fetchRules();
    }, [fetchRules]);

    const handleAddNewRule = () => {
        setEditingRule(null);
        setShowRuleForm(true);
    };

    const handleEditRule = (rule) => {
        setEditingRule(rule);
        setShowRuleForm(true);
    };

    const handleSaveRule = async (formData) => {
        setIsLoading(true); // Keep for form's internal loading state
        setError('');
        try {
            if (editingRule && editingRule.id) {
                console.log("Pricing Form Data:", formData)
                await updatePricingRule(categorySettingId, editingRule.id, formData, locale);
                toast.success("Rule updated successfully!");
            } else {
                console.log("Create Pricing Form Data:", formData)
                await createPricingRule(categorySettingId, formData, locale);
                toast.success("Rule created successfully!");
            }
            setShowRuleForm(false);
            setEditingRule(null);
            fetchRules(); 
        } catch (err) {
            const errorMsg = `Failed to save rule: ${err.response?.data?.detail || err.message}`;
            setError(errorMsg); // Show error in the Alert below
            toast.error(errorMsg);
            console.error("Save rule error:", err);
        } finally {
            // setIsLoading(false); //isLoading for this section is mainly for fetching
        }
    };

    const handleDeleteRule = async (ruleId) => {
        if (window.confirm("Are you sure you want to delete this pricing rule?")) {
            // Consider using shadcn/ui AlertDialog for a themed confirmation
            setIsLoading(true); // Indicate global loading for this section
            setError('');
            try {
                await deletePricingRule(categorySettingId, ruleId);
                toast.success("Rule deleted successfully!");
                fetchRules();
            } catch (err) {
                 const errorMsg = `Failed to delete rule: ${err.response?.data?.detail || err.message}`;
                 setError(errorMsg);
                toast.error(errorMsg);
                console.error("Delete rule error:", err);
            } finally {
                setIsLoading(false);
            }
        }
    };

    if (!canEditRules && rules.length === 0 && !isLoading) {
         return (
            <Alert variant="default" className="mt-6"> {/* Using default alert for info [cite: 26] */}
                <Terminal className="h-4 w-4" /> {/* Example icon */}
                <AlertTitle>Information</AlertTitle>
                <AlertDescription>
                    Variable (rule-based) pricing is currently disabled for this category. No rules to display or manage.
                </AlertDescription>
            </Alert>
         );
    }
    
    return (
        <div className="pt-6 border-t mt-6"> {/* Using pt-6 to match shadcn/ui CardFooter typical spacing */}
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-foreground">Pricing Rules</h3> {/* [cite: 1] */}
                {canEditRules && (
                    <Button onClick={handleAddNewRule} variant="default" size="sm" disabled={isLoading}>
                        Add New Rule
                    </Button> // [cite: 10, 13]
                )}
            </div>

            {error && (
                 <Alert variant="destructive" className="mb-4"> {/* [cite: 26] */}
                    <Terminal className="h-4 w-4" /> {/* Example icon */}
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            {isLoading && !showRuleForm ? ( // Only show section loading if form isn't also showing its own
                <p className="text-sm text-muted-foreground">Loading rules...</p> // [cite: 1]
            ) : (
                <PricingRuleList
                    rules={rules}
                    onEditRule={handleEditRule}
                    onDeleteRule={handleDeleteRule}
                    canEditRules={canEditRules}
                    questionList={questionList}
                />
            )}
            
            {!canEditRules && !isLoading && rules.length > 0 && ( // Show this only if there are rules but editing is off
                <Alert variant="default" className="mt-4 bg-accent/20 border-accent"> {/* Using accent for warning [cite: 1, 26] */}
                     <Terminal className="h-4 w-4" />
                     <AlertTitle className="text-accent-foreground">Editing Disabled</AlertTitle> {/* [cite: 1] */}
                    <AlertDescription className="text-accent-foreground/80"> {/* [cite: 1] */}
                        Rule editing is disabled because "Variable (Rule-based) Pricing" is turned off in the category settings above.
                    </AlertDescription>
                </Alert>
            )}

            {showRuleForm && ( // The PricingRuleForm is now a Dialog, so it handles its own visibility/overlay
                <PricingRuleForm
                    initialData={editingRule}
                    onSave={handleSaveRule}
                    onCancel={() => {
                        setShowRuleForm(false);
                        setEditingRule(null);
                        setError(''); // Clear any form-specific errors shown in the section
                    }}
                    isLoading={isLoading} // Pass section's loading for form's button states if needed
                    questionList={questionList}
                />
            )}
        </div>
    );
}